import api from '../api/client';
import { apiDelete, apiDeleteWithResponse, apiGet, apiPost, apiPut } from '@/services/api/baseService';
import type {
  CrmCustomer,
  CrmContact,
  CrmCustomerWithContacts,
  CreateCustomerDto,
  CreateContactDto,
  CrmStats,
  CrmSignupDomainRow,
} from '@/types/crm';

export const crmService = {
  // Kunder
  async getCustomers(): Promise<CrmCustomer[]> {
    return apiGet<CrmCustomer[]>('/api/crm/customers');
  },

  async getCustomer(id: string): Promise<CrmCustomerWithContacts> {
    return apiGet<CrmCustomerWithContacts>(`/api/crm/customers/${id}`);
  },

  async getCustomerById(id: string): Promise<CrmCustomerWithContacts> {
    return apiGet<CrmCustomerWithContacts>(`/api/crm/customers/${id}`);
  },

  async createCustomer(data: CreateCustomerDto): Promise<CrmCustomer> {
    return apiPost<CrmCustomer>('/api/crm/customers', data);
  },

  async updateCustomer(id: string, data: Partial<CreateCustomerDto>): Promise<CrmCustomer> {
    return apiPut<CrmCustomer>(`/api/crm/customers/${id}`, data);
  },

  async deleteCustomer(id: string): Promise<void> {
    return apiDelete(`/api/crm/customers/${id}`);
  },

  // Kontakter
  async getContacts(customerId: string): Promise<CrmContact[]> {
    return apiGet<CrmContact[]>(`/api/crm/customers/${customerId}/contacts`);
  },

  async createContact(
    customerId: string,
    data: CreateContactDto,
    sendWelcomeEmail = false
  ): Promise<{ success: boolean; data: CrmContact; userCreated?: boolean; warning?: string }> {
    const response = await api.post(`/api/crm/customers/${customerId}/contacts`, {
      ...data,
      sendWelcomeEmail,
    });
    return response.data;
  },

  async updateContact(id: string, data: Partial<CreateContactDto>): Promise<CrmContact> {
    return apiPut<CrmContact>(`/api/crm/contacts/${id}`, data);
  },

  async deleteContact(id: string): Promise<void> {
    return apiDelete(`/api/crm/contacts/${id}`);
  },

  // Statistik
  async getStats(): Promise<CrmStats> {
    return apiGet<CrmStats>('/api/crm/stats');
  },

  // Organisationsstatistik för en kund
  async getCustomerOrgStats(customerId: string): Promise<{
    vehicles: { total: number; active: number };
    devicesByType: { type: string; count: number }[];
    users: number;
    lockers: number;
    devices: number;
    flinkeyBoxes: number;
    registeredCables: number | null;
  }> {
    return apiGet(`/api/crm/customers/${customerId}/org-stats`);
  },

  // SCB/Checkbiz Företagsregister data
  async getCustomerScbData(customerId: string): Promise<{
    data: {
      organisationsnummer: string;
      foretagsnamn?: string;
      adress?: string;
      postnummer?: string;
      postort?: string;
      sni_kod?: string;
      sni_text?: string;
      storleksklass?: string;
      antal_anstallda?: number;
      arsomsattning?: number;
      verksamhetsar?: number;
      status?: string;
    } | null;
    companyGroup: string[];
    companyGroupList: Array<{
      id: string;
      org_number: string;
      company_name: string;
      level: number;
      percent: number | null;
      percent_text: string | null;
      data_source: string;
      created_at: string;
      updated_at: string;
    }>;
    vehiclesByType: {
      total: number;
      byBrand: { brand: string; count: number }[];
      byFuelType: { fuelType: string; count: number }[];
      byModel: { model: string; brand: string; count: number }[];
    };
    source?: 'checkbiz' | 'scb' | 'none';
  }> {
    const response = await api.get(`/api/crm/customers/${customerId}/scb-data`);
    return {
      data: response.data.data,
      companyGroup: response.data.companyGroup || [],
      companyGroupList: response.data.companyGroupList || [],
      vehiclesByType: response.data.vehiclesByType || {
        total: 0,
        byBrand: [],
        byFuelType: [],
        byModel: []
      },
      source: response.data.source || 'none'
    };
  },

  // Koncernstruktur
  async getCompanyGroup(customerId: string): Promise<Array<{
    id: string;
    org_number: string;
    company_name: string;
    level: number;
    percent: number | null;
    percent_text: string | null;
    data_source: string;
    created_at: string;
    updated_at: string;
  }>> {
    return apiGet(`/api/crm/customers/${customerId}/company-group`);
  },

  async searchCompanies(who: string, where?: string): Promise<{
    results: {
      organisationsnummer: string;
      foretagsnamn: string;
      adress?: string;
      postnummer?: string;
      postort?: string;
      telefon?: string;
      bolagsform?: string;
      status?: string;
      co?: string;
      latitude?: string;
      longitude?: string;
    }[];
    totalFound: number;
    count: number;
  }> {
    const params = new URLSearchParams();
    if (who) params.append('who', who);
    if (where) params.append('where', where);
    const response = await api.get(`/api/crm/company-search?${params.toString()}`);
    return {
      results: response.data.data || [],
      totalFound: response.data.totalFound || 0,
      count: response.data.count || 0,
    };
  },

  // Organisationsnummer för fordonshantering
  async getVehicleOrgNumbers(customerId: string): Promise<Array<{
    id: string;
    org_number: string;
    company_name: string | null;
    source: 'koncern' | 'manual';
    created_at: string;
  }>> {
    return apiGet(`/api/crm/customers/${customerId}/vehicle-org-numbers`);
  },

  async addVehicleOrgNumbersFromKoncern(customerId: string): Promise<{
    data: Array<{ id: string; org_number: string; company_name: string | null; source: 'koncern' | 'manual'; created_at: string }>;
    message: string;
  }> {
    const response = await api.post(`/api/crm/customers/${customerId}/vehicle-org-numbers/from-koncern`);
    return response.data;
  },

  async addVehicleOrgNumber(customerId: string, orgNumber: string, companyName?: string): Promise<Array<{
    id: string;
    org_number: string;
    company_name: string | null;
    source: 'koncern' | 'manual';
    created_at: string;
  }>> {
    return apiPost(`/api/crm/customers/${customerId}/vehicle-org-numbers`, {
      org_number: orgNumber,
      company_name: companyName,
    });
  },

  async removeVehicleOrgNumber(customerId: string, orgNumberId: string): Promise<Array<{
    id: string;
    org_number: string;
    company_name: string | null;
    source: 'koncern' | 'manual';
    created_at: string;
  }>> {
    return apiDeleteWithResponse(`/api/crm/customers/${customerId}/vehicle-org-numbers/${orgNumberId}`);
  },

  async getSignupDomains(customerId: string): Promise<CrmSignupDomainRow[]> {
    return apiGet<CrmSignupDomainRow[]>(`/api/crm/customers/${customerId}/signup-domains`);
  },

  async addSignupDomain(customerId: string, domain: string): Promise<CrmSignupDomainRow> {
    return apiPost<CrmSignupDomainRow>(`/api/crm/customers/${customerId}/signup-domains`, { domain });
  },

  async removeSignupDomain(customerId: string, domainRowId: string): Promise<void> {
    await apiDelete(`/api/crm/customers/${customerId}/signup-domains/${domainRowId}`);
  },

  async fetchAndSaveCompanyGroup(customerId: string, orgNumber?: string): Promise<{
    data: Array<{
      org_number: string;
      company_name: string;
      level: number;
      percent?: number | null;
      percent_text?: string | null;
    }>;
    companyData?: any;
    source: 'checkbiz' | 'scb';
    message: string;
  }> {
    const response = await api.post(`/api/crm/customers/${customerId}/company-group/fetch`, orgNumber ? { org_number: orgNumber } : {});
    return response.data;
  }
};

